import React, {useState} from 'react';
import { Chart } from 'react-chartjs-2';
import { Chart as ChartJS, BarController, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend } from 'chart.js'
import { useTranslation } from 'react-i18next';
import { SelectButton } from 'primereact/selectbutton';
import { useMediaQuery } from 'react-responsive'; // Importar useMediaQuery desde react-responsive


ChartJS.register(BarController,BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

// Definición de un componente funcional
const Annual = ({ data, selectedTimeChunk, setSelectedTimeChunk, selectedUser }) => {
    const { t } = useTranslation();
    const [statisticsData, setStatisticsData] = useState(data);
    const options = [
        { name: t('WEEK'), code: 'WEEK'},
        { name: t('MONTH'), code: 'MONTH'}
    ];
    const [option, setOption] = useState( selectedTimeChunk );


    const weeks = [
        t('Monday'),
        t('Tuesday'),
        t('Wednesday'),
        t('Thursday'),
        t('Friday'),
        t('Saturday'),
        t('Sunday'),
    ]

    const months = [
        t('January'),
        t('February'),
        t('March'),
        t('April'),
        t('May'),
        t('June'),
        t('July'),
        t('August'),
        t('September'),
        t('October'),
        t('November'),
        t('December'),
    ];

    const handleTimeChunkChange = ({value} ) => {
        setOption(t(value.name))
        setSelectedTimeChunk(value.code);
    };

    const chartData = {
        // Generating labels from 1 up to the number of weeks or months
        labels: Array.from({ length: selectedTimeChunk === 'WEEK' ? 52 : 12 }, (_, i) => {
            if (selectedTimeChunk === 'WEEK') return t('Week ') + (i + 1);
            else return  months[i]
        }),
        datasets: [
            {
                label: t('current-year'),
                // Map the data array so that it aligns perfectly with the labels
                data: Array.from({ length: selectedTimeChunk === 'WEEK' ? 52 : 12 }, (_, i) => {
                    // Directly use the index + 1 as the key to access data
                    return statisticsData[selectedUser]?.[new Date().getFullYear()]?.[i + 1] || 0;
                }),
                backgroundColor: '#00A58D',
            },
            {
                label: t('Last Year'),
                data: Array.from({ length: selectedTimeChunk === 'WEEK' ? 52 : 12 }, (_, i) => {
                    return statisticsData[selectedUser]?.[new Date().getFullYear() - 1]?.[i + 1] || 0;
                }),
                backgroundColor: '#d9e6e4',
            }
        ]
    };

    const isMobile = useMediaQuery({ maxWidth: 768 }); // Define el ancho máximo para considerar como móvil
    const legendPosition = isMobile ? 'bottom' : 'right';
    const legendAlign = isMobile ? 'center' : 'start';

    const chartOptions = {
        responsive: true,
        aspectRatio: 1,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    color: 'white', // can also use hex color codes
                    // stepSize: 1
                    font: {
                        size: 12, // Tamaño de fuente de las etiquetas del eje X
                        family: "'Montserrat', sans-serif",
                    },
                    stepSize: 10, // Intervalo entre las etiquetas del eje X
                    padding: 20,
                },
                grid: {
                    display: false, // Desactiva la cuadrícula del eje X
                },
                border: {
                    display: true,
                    color: '#BABABA', // Color de la línea del eje X
                    width: 1, // Ancho de la línea del eje X
                }
            },
            x: {
                ticks: {
                    color: 'white',
                    autoSkip: true,
                    maxTicksLimit: selectedTimeChunk === 'WEEK' ? 52 : 12,
                    font: {
                        size: 12, // Tamaño de fuente de las etiquetas del eje X
                        family: "'Montserrat', sans-serif",
                    },
                    minRotation: selectedTimeChunk === 'WEEK' ? 90 : 0,
                },
                grid: {
                    display: false, // Desactiva la cuadrícula del eje X
                },
                border: {
                    display: true,
                    color: '#BABABA', // Color de la línea del eje X
                    width: 1, // Ancho de la línea del eje X
                },
            }
        },
        plugins: {
            legend: {
                display: true,
                position: legendPosition, // 'top', 'left', 'bottom', 'right'
                align: legendAlign,
                useBorderRadius: true,
                labels: {
                    font: {
                        size: 14,
                        family: "'Montserrat', sans-serif",
                    },
                    color: 'rgb(255,255,255)', // Color de las etiquetas de la leyenda
                    padding: 20, // Espacio alrededor de las etiquetas
                    margin: 10,
                    boxWidth: 15, // Ancho del cuadro de color de la leyenda
                },
            },
            layout: {
                padding: {
                    // top: 45,
                    // bottom: 45, // Añade espacio entre el gráfico y la leyenda
                    // right: 45, // Añade espacio entre el gráfico y la leyenda
                    // left: 45, // Añade espacio entre el gráfico y la leyenda
                },
            },
            customBorder: {
                color: 'white',
                offset: {
                    left: 5,
                    right: 5
                }
            },
            customMargin: true
        },
    };

    const customBorder =
        [
            {
                id: 'customBorder',
                afterDraw: (chart, args, opts) => {
                    const { ctx, legend } = chart;
                    ctx.strokeStyle = opts.color || Chart.defaults.color;
                    const borderRadius = 10;
                    const height = 85
                    const top = legend.top + 1
                    const right = 10;
                    if( !isMobile ){
                        ctx.save()
                        ctx.beginPath();
                        ctx.moveTo(legend.left + borderRadius , top)
                        ctx.lineTo((legend.right-right) - borderRadius, top)
                        ctx.quadraticCurveTo((legend.right-right), top, (legend.right-right), top + borderRadius)
                        ctx.lineTo((legend.right-right) , height - borderRadius)
                        ctx.quadraticCurveTo((legend.right-right), height, (legend.right-right) - borderRadius, height )
                        ctx.lineTo(legend.left + borderRadius, height)
                        ctx.quadraticCurveTo( legend.left, height, legend.left, height - borderRadius )
                        ctx.lineTo(legend.left, top + borderRadius )
                        ctx.quadraticCurveTo( legend.left, top, legend.left + borderRadius, top )
                        ctx.stroke()
                    }
                }
            },
            {
                id: 'customMargin',

                beforeInit( chart, args, options) {
                    const originalFit = chart.legend.fit;

                    chart.legend.fit = function fit() {
                        originalFit.bind(chart.legend)();
                        let width = this.width += 20;
                        return width
                    }
                }
            }
        ]

    return (
        <div className="w-11/12 m-auto mt-3">

            <div className="mimetika-chart border-white border-1 rounded-md px-2 py-6 lg:px-6 lg:py-3">

                <Chart type="bar"
                       data={chartData}
                       options={chartOptions}
                       width="auto"
                       height="auto"
                       plugins={customBorder}
                       className="mimetika-chart-canvas max-h-60"
                />
                <SelectButton
                    value={option}
                    onChange={handleTimeChunkChange}
                    options={options}
                    optionLabel="name"
                    className={`mimetika-select-button mt-3`}
                />
            </div>
        </div>
    );
}

export default Annual;
